import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, 
         isSameMonth, isSameDay, isSameWeek, parseISO, addMonths, subMonths } from 'date-fns';
import { CalendarEventType } from './types';
import './CalendarViewModern.css'; // Import the new modern CSS file

// Types
interface EventType {
  id: string | number;
  title: string;
  name?: string; // Some APIs use name instead of title
  description?: string;
  duration?: number;  // Make this optional
  length?: number;    // Support legacy length property
  schedule?: {
    days: Array<{
      date: string;
      weekday: string;
      weekday_number: number;
      slot_ranges: Array<{ start: string, end: string }>;
      duration_slots: Record<string, string[]>;
    }>;
    timezone: string;
  };
}

interface BookedTimeSlot {
  time: string;
  time_short: string;
  eventTypeId: number | string;
  eventTypeName?: string;
  eventTypeSlug?: string;
  bookingId?: number;
  duration?: number;
}

interface BookedSlotsData {
  status: string;
  data: {
    booked_times_by_day: Record<string, BookedTimeSlot[]>;
    timezone: string;
  };
}

interface BookedSlot {
  time: string;         // Full ISO format
  time_short: string;   // HH:MM format
  eventTypeId: string;  // Cal.com event type ID
  duration: number;
}

interface CalendarViewProps {
  events: (EventType | CalendarEventType)[];
    timezone: string;
  onTimeSelect?: (event: any, date: Date, time: string, timeInfo?: any) => void;
  clientId?: string;
    initialBookings?: any[];
  instanceId?: string;
    preserveUTC?: boolean;
  bookedSlots?: BookedSlotsData; // New prop for booked slots
  forceLightMode?: boolean; // New prop to force light mode
}

const CalendarView: React.FC<CalendarViewProps> = ({ 
    events, 
    timezone, 
    onTimeSelect, 
  clientId,
    initialBookings = [],
  instanceId,
  preserveUTC = false,
  bookedSlots,
  forceLightMode = false
}) => {
  // Essential state
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [noSlotsAvailable, setNoSlotsAvailable] = useState<boolean>(false);
  const [bookedSlotsByDay, setBookedSlotsByDay] = useState<Record<string, BookedSlot[]>>({});
  const [selectedTimeInfo, setSelectedTimeInfo] = useState<any>(null);

  // State to track if we're in weekly view (mobile/tablet) vs monthly view (desktop)
  const [isWeeklyView, setIsWeeklyView] = useState<boolean>(window.innerWidth <= 1023);
  
  // Add new state for tracking if we need to refresh after event selection
  const [pendingEventRefresh, setPendingEventRefresh] = useState<boolean>(false);
  const [lastSelectedEvent, setLastSelectedEvent] = useState<EventType | null>(null);
  
  // Add these state variables
  const [refreshCount, setRefreshCount] = useState<number>(0);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState<number>(0);
  
  // Track window resize to switch between weekly and monthly views
    useEffect(() => {
    const handleResize = () => {
      const shouldShowWeeklyView = window.innerWidth <= 1023;
      setIsWeeklyView(shouldShowWeeklyView);
    };
    
    // Set initial value
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Helper function to format time for display (12-hour format with AM/PM)
  const formatTimeForDisplay = (timeString: string): string => {
    if (!timeString) return '';
    
    try {
      // Handle case where timeString is already in 12-hour format
      if (timeString.toLowerCase().includes('am') || timeString.toLowerCase().includes('pm')) {
        return timeString;
      }

      // Parse 24-hour time format
      const [hoursStr, minutesStr] = timeString.split(':');
      const hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);

      if (isNaN(hours) || isNaN(minutes)) {
        return timeString;
      }

      // Convert to 12-hour format
      const period = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM
      return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      console.error('[Calendar] Error formatting time:', error);
      return timeString;
    }
  };

  // Helper type guard function to check if the event is a CalendarEventType
  const isCalendarEventType = (event: any): event is CalendarEventType => {
    return 'length' in event && !('schedule' in event);
  };

  // Adapter function to normalize event data
  const normalizeEvent = (event: EventType | CalendarEventType): EventType => {
    // Convert CalendarEventType to EventType format
    if (isCalendarEventType(event)) {
      return {
        ...event,
        id: typeof event.id === 'number' ? String(event.id) : event.id,
        duration: event.length || 30,
        name: event.title,
        schedule: {
          days: [],
          timezone: timezone
        }
      } as EventType;
    }
    return event as EventType;
  };

  // Modify the useEffect that processes booked slots to use timestamp checks
  useEffect(() => {
    // Add debug logging
    console.log('[BOOKED SLOTS EFFECT] Running with data:', bookedSlots);
    
    // Skip if no data is provided
    if (!bookedSlots) {
      console.log('[BOOKED SLOTS PROCESSING] No data provided');
      setBookedSlotsByDay({});
      return;
    }
    
    // Process the data if it matches our expected structure
    if (bookedSlots.status === 'success' && bookedSlots.data && bookedSlots.data.booked_times_by_day) {
      const numDates = Object.keys(bookedSlots.data.booked_times_by_day).length;
      console.log(`[BOOKED SLOTS PROCESSING] Processing ${numDates} dates with booked slots`);
      
      // If no booked dates, just set empty object
      if (numDates === 0) {
        console.log('[BOOKED SLOTS PROCESSING] No booked dates found');
        setBookedSlotsByDay({});
        return;
      }
      
      // Format the booked slots data
      const formattedBookedSlots: Record<string, BookedSlot[]> = {};
      
      Object.entries(bookedSlots.data.booked_times_by_day).forEach(([date, slots]) => {
        console.log(`[BOOKED SLOTS PROCESSING] Date ${date} has ${slots.length} booked slots`);
        
        if (!slots || !Array.isArray(slots) || slots.length === 0) {
          console.log(`[BOOKED SLOTS PROCESSING] No valid slots for date ${date}`);
          return;
        }
        
        formattedBookedSlots[date] = slots.map((slot: BookedTimeSlot) => ({
          time: `${date}T${slot.time}`,
          time_short: slot.time_short,
          eventTypeId: String(slot.eventTypeId),
          // Include the duration if available, or default to 30 minutes
          duration: slot.duration || 30
        }));
      });
      
      console.log('[BOOKED SLOTS PROCESSING] Final formatted data:', formattedBookedSlots);
      
      // Update the state with formatted booked slots
      setBookedSlotsByDay(formattedBookedSlots);
      
      // Only consider refreshing if we have the necessary data and enough time has passed
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshTimestamp;
      const shouldRefresh = selectedDate && 
                          selectedEvent && 
                          !isRefreshing && 
                          timeSinceLastRefresh > 5000; // At least 5 seconds between refreshes
      
      if (shouldRefresh) {
        console.log('[BOOKED SLOTS PROCESSING] Sufficient time has passed, refreshing selected date:', 
                    format(selectedDate, 'yyyy-MM-dd'),
                    'Last refresh was', Math.floor(timeSinceLastRefresh / 1000), 'seconds ago');
        
        // Set refreshing flag and update timestamp
        setIsRefreshing(true);
        setLastRefreshTimestamp(now);
        
        // Rather than calling handleDateSelect directly, we'll mark for a refresh
        // This avoids circular dependencies
        console.log('[CALENDAR REFRESH] Marking for refresh on next render');
        setPendingEventRefresh(true);
      } else if (selectedDate && selectedEvent) {
        // Log why we're not refreshing
        if (isRefreshing) {
          console.log('[BOOKED SLOTS PROCESSING] Skipping refresh - already refreshing');
        } else if (timeSinceLastRefresh <= 5000) {
          console.log('[BOOKED SLOTS PROCESSING] Skipping refresh - last refresh was only', 
                      Math.floor(timeSinceLastRefresh / 1000), 'seconds ago');
        }
      }
    } else {
      console.log('[BOOKED SLOTS PROCESSING] Invalid data structure:', bookedSlots);
      setBookedSlotsByDay({});
    }
  }, [bookedSlots, selectedDate, selectedEvent, isRefreshing, lastRefreshTimestamp]);

  // Process initial bookings into a structure indexed by date
  useEffect(() => {
    if (initialBookings && initialBookings.length > 0) {
      const bookingsByDate: Record<string, BookedSlot[]> = {};
      
      initialBookings.forEach(booking => {
        if (booking.time || booking.startTime) {
          // Extract date from the booking time
          const timeField = booking.time || booking.startTime;
          const datePart = timeField.split('T')[0];
          if (!bookingsByDate[datePart]) {
            bookingsByDate[datePart] = [];
          }
          
          // Normalize the booking data structure
          const normalizedBooking: BookedSlot = {
            time: booking.time || booking.startTime,
            time_short: booking.time_short || format(new Date(booking.startTime), 'HH:mm'),
            eventTypeId: String(booking.eventTypeId || (booking.eventType?.id) || ''),
            duration: booking.duration || 30
          };
          
          bookingsByDate[datePart].push(normalizedBooking);
        }
      });
      
      // Merge with any existing booked slots
      setBookedSlotsByDay(prev => ({...prev, ...bookingsByDate}));
    }
  }, [initialBookings]);

  // Select first event when component mounts
  useEffect(() => {
    if (events.length > 0 && !selectedEvent) {
      const normalizedEvent = normalizeEvent(events[0]);
      setSelectedEvent(normalizedEvent);
    }
  }, [events, selectedEvent]);

  // Check if a date has available slots based on the event schedule
  const isDateAvailable = useCallback((date: Date, event: EventType | null): boolean => {
    if (!event || !event.schedule) return false;
    
    try {
      // Don't allow past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (date < today) return false;
          
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayName = format(date, 'EEEE'); // e.g., Monday
      
      // Check if the date is in the schedule's days array
      if (event.schedule.days && event.schedule.days.length > 0) {
        // Look for an exact date match
        const exactDateMatch = event.schedule.days.find(day => day.date === dateStr);
        if (exactDateMatch) return true;
        
        // Look for a weekday match
        const weekdayMatch = event.schedule.days.find(day => 
                  day.weekday && day.weekday.toLowerCase() === dayName.toLowerCase()
              );
        if (weekdayMatch) return true;
      }
      
      return false;
    } catch (err) {
      console.error('[Calendar] Error checking date availability:', err);
      return false;
    }
  }, []);

  // Remove the handleDateSelect call from the auto-select useEffect
  useEffect(() => {
    if (selectedEvent && selectedEvent.schedule && selectedEvent.schedule.days && selectedEvent.schedule.days.length > 0) {
      // Find the first available date that has slots and is not in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Try to find a date with available slots
      let firstAvailableDate: Date | null = null;
      
      for (const day of selectedEvent.schedule.days) {
        try {
          const date = parseISO(day.date);
          
          // Skip past dates
          if (date < today) continue;
          
          // Check if this date has available slots
          if (isDateAvailable(date, selectedEvent)) {
            firstAvailableDate = date;
            break;
          }
        } catch (err) {
          console.error('[Calendar] Error parsing date:', err);
        }
      }
      
      // If we found an available date, select it
      if (firstAvailableDate) {
        setSelectedDate(firstAvailableDate);
        setCurrentMonth(startOfMonth(firstAvailableDate));
        
        // DON'T call handleDateSelect directly from here - 
        // just mark that we selected a date and let the state change
        // trigger the appropriate update
        // Instead of handleDateSelect(firstAvailableDate);
        console.log('Selected first available date', format(firstAvailableDate, 'yyyy-MM-dd'));
        // The pendingEventRefresh will trigger the refresh in the next cycle
        setPendingEventRefresh(true);
      } else {
        // If no available date found, just set the current month to today
        setCurrentMonth(startOfMonth(new Date()));
      }
    }
  }, [selectedEvent, isDateAvailable]);

  // Get available time slots for a specific date
  const getAvailableTimeSlots = useCallback((date: Date, event: EventType | null): string[] => {
    if (!event || !date || !event.schedule) return [];
    
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayName = format(date, 'EEEE');
      
      // Find the matching day in the schedule
      let matchingDay = event.schedule.days.find(day => day.date === dateStr);
      
      // If no exact date match, try finding by weekday
      if (!matchingDay) {
        matchingDay = event.schedule.days.find(day => 
          day.weekday && day.weekday.toLowerCase() === dayName.toLowerCase()
        );
      }
      
      if (!matchingDay) return [];
      
      // Get duration-specific slots if available
      const durationValue = event.duration || 30; // Default to 30 minutes if undefined
      const duration = String(durationValue);
      
      if (matchingDay.duration_slots && matchingDay.duration_slots[duration]) {
        return matchingDay.duration_slots[duration];
      }
      
      // Otherwise, derive slots from time ranges
      const slots: string[] = [];
      
      // Make sure duration is a number when used in calculations
      const eventDuration = durationValue;
      
      if (matchingDay.slot_ranges && matchingDay.slot_ranges.length > 0) {
        matchingDay.slot_ranges.forEach(range => {
          const [startHour, startMinute] = range.start.split(':').map(Number);
          const [endHour, endMinute] = range.end.split(':').map(Number);
          
          const startTime = startHour * 60 + startMinute;
          const endTime = endHour * 60 + endMinute;
          
          // Generate slots at specified duration intervals
          for (let time = startTime; time < endTime; time += eventDuration) {
            const hours = Math.floor(time / 60);
            const minutes = time % 60;
            slots.push(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
          }
        });
      }
      
      return slots;
                    } catch (error) {
      console.error('[Calendar] Error getting available time slots:', error);
      return [];
    }
  }, []);

  // Filter out booked slots
  const filterBookedSlots = useCallback((slots: string[], date: Date | null): string[] => {
    if (!date || !selectedEvent) return slots;
    
    const dateStr = format(date, 'yyyy-MM-dd');
    const bookedSlotsForDay = bookedSlotsByDay[dateStr] || [];
    
    console.log(`DEBUG - Using all ${bookedSlotsForDay.length} booked slots for filtering, regardless of event type`);
    
    // If no booked slots for this day, return all slots
    if (bookedSlotsForDay.length === 0) {
      console.log(`No booked slots for ${dateStr}, returning all slots.`);
      return slots;
    }
    
    // Extract time_short values from all booked slots
    const bookedTimes = bookedSlotsForDay.map(slot => slot.time_short);
    console.log('Booked times to filter out:', bookedTimes);
    
    // Get the current event's duration in minutes (always use fresh selectedEvent data)
    const eventDurationMinutes = selectedEvent.duration || 30; // Default to 30 min if not specified
    
    // Filter out the booked slots AND any slots that would overlap with booked slots
    const filteredSlots = slots.filter(slot => {
      // Parse this slot's time
      const [slotHourStr, slotMinuteStr] = slot.split(':');
      const slotHour = parseInt(slotHourStr, 10);
      const slotMinute = parseInt(slotMinuteStr, 10);
      const slotTotalMinutes = slotHour * 60 + slotMinute;
      
      // Calculate end time of this slot based on event duration
      const slotEndTotalMinutes = slotTotalMinutes + eventDurationMinutes;
      
      // Check if this slot is booked in any format (exact match)
      const timeWithoutLeadingZero = slot.replace(/^0/, '');
      const timeWithSeconds = slot.split(':').length === 2 ? `${slot}:00` : slot;
      
      // Check for exact matches (backward compatibility)
      const isExactlyBooked = bookedTimes.some(bookedTime => {
        return (
          bookedTime === slot ||
          bookedTime === timeWithoutLeadingZero ||
          bookedTime === timeWithSeconds
        );
      });
      
      if (isExactlyBooked) {
        console.log(`Filtering out exactly booked slot: ${slot}`);
        return false;
      }
      
      // Now check for overlaps with any booked slot
      const hasOverlap = bookedSlotsForDay.some(bookedSlot => {
        // Parse booked slot time
        const [bookedHourStr, bookedMinuteStr] = bookedSlot.time_short.split(':');
        const bookedHour = parseInt(bookedHourStr, 10);
        const bookedMinute = parseInt(bookedMinuteStr, 10);
        const bookedTotalMinutes = bookedHour * 60 + bookedMinute;
        
        // Use the actual booked duration if available
        const bookedDurationMinutes = bookedSlot.duration || 30;
        const bookedEndTotalMinutes = bookedTotalMinutes + bookedDurationMinutes;
        
        // Check if there's any overlap between this slot and the booked slot
        const slotOverlapsWithBooked = (
          // Slot starts during a booked time
          (slotTotalMinutes >= bookedTotalMinutes && slotTotalMinutes < bookedEndTotalMinutes) ||
          // Slot ends during a booked time
          (slotEndTotalMinutes > bookedTotalMinutes && slotEndTotalMinutes <= bookedEndTotalMinutes) ||
          // Slot completely contains a booked time
          (slotTotalMinutes <= bookedTotalMinutes && slotEndTotalMinutes >= bookedEndTotalMinutes)
        );
        
        if (slotOverlapsWithBooked) {
          console.log(`Slot ${slot} (${slotTotalMinutes}-${slotEndTotalMinutes} mins) overlaps with booked slot ${bookedSlot.time_short} (${bookedTotalMinutes}-${bookedEndTotalMinutes} mins), duration: ${bookedDurationMinutes}mins`);
        }
        
        return slotOverlapsWithBooked;
      });
      
      if (hasOverlap) {
        console.log(`Filtering out overlapping slot: ${slot}`);
        return false;
      }
      
      // Keep this slot if it doesn't match any booked time or overlap with any booked slot
      return true;
    });
    
    console.log(`Original slots: ${slots.length}, Filtered slots: ${filteredSlots.length}`);
    return filteredSlots;
  }, [selectedEvent, bookedSlotsByDay]);

    // Filter out past time slots for today
  const filterPastTimeSlots = useCallback((slots: string[], date: Date): string[] => {
            const now = new Date();
        const isToday = isSameDay(date, now);
        
    if (!isToday) return slots;
        
                const currentHour = now.getHours();
                const currentMinute = now.getMinutes();
                
        return slots.filter(slotTime => {
                    const [hourStr, minuteStr] = slotTime.split(':');
      const slotHour = parseInt(hourStr, 10);
      const slotMinute = parseInt(minuteStr, 10);
      
      // Add a buffer of 1 hour to the current time
      return (slotHour > currentHour + 1) || 
             (slotHour === currentHour + 1 && slotMinute >= currentMinute);
    });
  }, []);

  // Re-add the Refresh handling useEffect AFTER all the functions it depends on have been declared
  useEffect(() => {
    if (pendingEventRefresh && selectedDate && !isRefreshing) {
      const now = Date.now();
      const timeSinceLastRefresh = now - lastRefreshTimestamp;
      
      if (timeSinceLastRefresh > 1000) { // At least 1 second since last refresh
        console.log('Executing pending refresh');
        setLastRefreshTimestamp(now);
        // Avoid the circular dependency by directly loading slots instead of calling handleDateSelect
        const refreshDate = selectedDate;
        const refreshEvent = selectedEvent;
        
        if (refreshEvent) {
          setIsLoading(true);
          try {
            const dateStr = format(refreshDate, 'yyyy-MM-dd');
            console.log(`[REFRESH] Direct refresh for date: ${dateStr}`);
            
            // Get raw slots for the day
            let availableSlots = getAvailableTimeSlots(refreshDate, refreshEvent);
            console.log(`[REFRESH] Raw available slots (${availableSlots.length})`);
            
            // Filter booked slots
            availableSlots = filterBookedSlots(availableSlots, refreshDate);
            console.log(`[REFRESH] After filtering booked slots (${availableSlots.length})`);
            
            // Filter past slots if the selected date is today
            if (isSameDay(refreshDate, new Date())) {
              availableSlots = filterPastTimeSlots(availableSlots, refreshDate);
            }
            
            // Update state based on results
            if (availableSlots.length === 0) {
              setNoSlotsAvailable(true);
            } else {
              setNoSlotsAvailable(false);
              setTimeSlots(availableSlots);
            }
          } catch (error) {
            console.error('[REFRESH] Error refreshing time slots:', error);
          } finally {
            setIsLoading(false);
          }
        }
        
        // Clear the pending refresh flag
        setPendingEventRefresh(false);
        
        // Reset the refreshing state
        setTimeout(() => {
          setIsRefreshing(false);
        }, 100);
      } else {
        console.log('Delaying refresh - too soon since last refresh');
        // Schedule it for later
        setTimeout(() => {
          if (pendingEventRefresh) {
            console.log('Time to execute delayed refresh');
            // Just re-trigger the effect by updating the timestamp
            setLastRefreshTimestamp(Date.now());
          }
        }, 1500);
      }
    }
  }, [pendingEventRefresh, selectedDate, selectedEvent, isRefreshing, lastRefreshTimestamp, 
      getAvailableTimeSlots, filterBookedSlots, filterPastTimeSlots]);

  // Handle date selection - make sure this has priority over automatic refreshes
  const handleDateSelect = useCallback((date: Date) => {
    console.log('Date selected:', format(date, 'yyyy-MM-dd'), 'with event:', selectedEvent?.title);
    setIsLoading(true);
    setSelectedDate(date);
    setSelectedTimeSlot(null);
    // Reset refresh tracking when manually selecting a date
    setIsRefreshing(false);
    setLastRefreshTimestamp(Date.now());
    
    try {
      // Load time slots for the selected date and event
      if (selectedEvent) {
        const dateStr = format(date, 'yyyy-MM-dd');
        console.log(`DEBUG - Selected date: ${dateStr}`);
        
        // Get raw slots for the day - show all available slots even if booked data isn't loaded
        let availableSlots = getAvailableTimeSlots(date, selectedEvent);
        console.log(`DEBUG - Raw available slots (${availableSlots.length}):`, availableSlots);
        
        // Don't block showing slots if booked data isn't loaded
        if (Object.keys(bookedSlotsByDay).length === 0) {
          console.log('Booked slots not loaded yet, showing all available slots');
          
          // Filter past slots if the selected date is today
          if (isSameDay(date, new Date())) {
            availableSlots = filterPastTimeSlots(availableSlots, date);
          }
          
          if (availableSlots.length > 0) {
            setNoSlotsAvailable(false);
            setTimeSlots(availableSlots);
          } else {
            setNoSlotsAvailable(true);
          }
          
          setIsLoading(false);
          return;
        }

        // Debug booked slots information
        const bookedForThisDay = bookedSlotsByDay[dateStr] || [];
        console.log(`DEBUG - Booked slots for day (${bookedForThisDay.length}):`, bookedForThisDay);
        console.log(`DEBUG - Current event duration being used for filtering: ${selectedEvent.duration || 30} minutes`);
        
        // Filter out booked slots
        availableSlots = filterBookedSlots(availableSlots, date);
        console.log(`DEBUG - After filtering booked slots (${availableSlots.length}):`, availableSlots);
        
        // Filter past slots if the selected date is today
        if (isSameDay(date, new Date())) {
          availableSlots = filterPastTimeSlots(availableSlots, date);
          console.log(`DEBUG - After filtering past times (${availableSlots.length}):`, availableSlots);
        }
        
        // Update state based on results
        if (availableSlots.length === 0) {
          setNoSlotsAvailable(true);
        } else {
          setNoSlotsAvailable(false);
          setTimeSlots(availableSlots);
        }
      }
    } catch (error) {
      console.error('[Calendar] Error loading time slots:', error);
      setNoSlotsAvailable(true);
    } finally {
      setIsLoading(false);
    }
  }, [selectedEvent, getAvailableTimeSlots, filterBookedSlots, filterPastTimeSlots, bookedSlotsByDay]);

  // Handle event selection - include resetting refresh state
  const handleEventSelect = useCallback((event: EventType | CalendarEventType) => {
    console.log('Event selected:', event);
    
    // Normalize the event to ensure it has a consistent structure
    const normalizedEvent = normalizeEvent(event);
    setSelectedEvent(normalizedEvent);
    setLastSelectedEvent(normalizedEvent);
    
    // Reset time slots when changing event type
    setSelectedTimeSlot(null);
    setTimeSlots([]);
    
    // Reset refresh tracking when manually selecting an event
    setIsRefreshing(false);
    setLastRefreshTimestamp(Date.now());
    
    // If we already have a selected date, mark for refresh instead of using setTimeout
    if (selectedDate) {
      console.log('Marking time slots for refresh with new event type');
      setPendingEventRefresh(true);
    }
    
    // The first available date will be auto-selected by the useEffect
  }, [selectedDate]);

  // Helper function to determine time of day
  const getTimeOfDay = (timeString: string): string => {
    const hour = parseInt(timeString.split(':')[0], 10);
    
    if (hour >= 5 && hour < 12) {
      return 'morning';
    } else if (hour >= 12 && hour < 17) {
      return 'afternoon';
            } else {
      return 'evening';
    }
  };

  // Calculate the end time based on start time and duration
  const calculateEndTime = useCallback((startTime: string): string => {
    if (!selectedEvent) return startTime;
    
    // Get duration from the event, defaulting to 30 minutes if not defined
    const duration = selectedEvent.duration || 30;
    const [hours, minutes] = startTime.split(':').map(Number);
    
    let endMinutes = minutes + duration;
    let endHours = hours;
    
    while (endMinutes >= 60) {
      endMinutes -= 60;
      endHours += 1;
    }
    
    endHours = endHours % 24;
    
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  }, [selectedEvent]);

  // Handle time slot selection
  const handleTimeSelect = useCallback((time: string) => {
    setSelectedTimeSlot(time);
    
    // Call the onTimeSelect callback if provided
    if (onTimeSelect && selectedEvent && selectedDate) {
      // Create the ISO time string correctly
      const [hours, minutes] = time.split(':').map(Number);
      const timeDate = new Date(selectedDate);
      timeDate.setHours(hours, minutes, 0, 0);
      
      // Create exact format: YYYY-MM-DDTHH:mm:00.000Z
      // This ensures we have exactly the format Cal.com API expects
      const year = timeDate.getUTCFullYear();
      const month = String(timeDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(timeDate.getUTCDate()).padStart(2, '0');
      const hour = String(timeDate.getUTCHours()).padStart(2, '0');
      const minute = String(timeDate.getUTCMinutes()).padStart(2, '0');
      const isoTime = `${year}-${month}-${day}T${hour}:${minute}:00.000Z`;
      
      // Get the end time
      const endTime = calculateEndTime(time);
      const [endHours, endMinutes] = endTime.split(':').map(Number);
      const endTimeDate = new Date(selectedDate);
      endTimeDate.setHours(endHours, endMinutes, 0, 0);
      
      // Create exact end time format
      const endYear = endTimeDate.getUTCFullYear();
      const endMonth = String(endTimeDate.getUTCMonth() + 1).padStart(2, '0');
      const endDay = String(endTimeDate.getUTCDate()).padStart(2, '0');
      const endHour = String(endTimeDate.getUTCHours()).padStart(2, '0');
      const endMinute = String(endTimeDate.getUTCMinutes()).padStart(2, '0');
      const endIsoTime = `${endYear}-${endMonth}-${endDay}T${endHour}:${endMinute}:00.000Z`;
      
      // Create a timeInfo object with all necessary information
      const timeInfo = {
        time: time,
        formattedTime: formatTimeForDisplay(time),
        isoTime: isoTime,
        endTime: endTime,
        endIsoTime: endIsoTime,
        duration: selectedEvent.duration || selectedEvent.length || 30
      };
      
      console.log('Booking timeInfo:', timeInfo);
      
      // Set the selected time info state for potential future use
      setSelectedTimeInfo(timeInfo);
      
      // Call the callback with all necessary information
      onTimeSelect(selectedEvent, selectedDate, time, timeInfo);
    }
  }, [onTimeSelect, selectedEvent, selectedDate, calculateEndTime, formatTimeForDisplay]);

  // Navigate to previous week/month
  const handlePrevMonth = useCallback(() => {
    // Use the isWeeklyView state instead of checking window.innerWidth directly
    if (isWeeklyView) {
      // Move back one week instead of one month
      setCurrentMonth(date => {
        const prevWeek = addDays(date, -7);
        return prevWeek;
      });
    } else {
      // Regular month navigation for larger screens
      setCurrentMonth(subMonths(currentMonth, 1));
    }
  }, [currentMonth, isWeeklyView]);

  // Navigate to next week/month
  const handleNextMonth = useCallback(() => {
    // Use the isWeeklyView state instead of checking window.innerWidth directly
    if (isWeeklyView) {
      // Move forward one week instead of one month
      setCurrentMonth(date => {
        const nextWeek = addDays(date, 7);
        return nextWeek;
      });
    } else {
      // Regular month navigation for larger screens
      setCurrentMonth(addMonths(currentMonth, 1));
    }
  }, [currentMonth, isWeeklyView]);

  // Render the calendar header (month/week navigation)
  const renderCalendarHeader = useCallback(() => {
    // Get current week range
    const weekStart = startOfWeek(currentMonth);
    const weekEnd = endOfWeek(currentMonth);
    const weekRange = `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
                        
                        return (
      <div className="calendar-header">
        <div>
          {isWeeklyView ? (
            <>
              <h3>Week of {format(weekStart, 'MMM d, yyyy')}</h3>
              <span className="date-range-display">{weekRange}</span>
            </>
          ) : (
            <>
              <h3>{format(currentMonth, 'MMMM yyyy')}</h3>
              <span className="date-range-display">{weekRange}</span>
            </>
          )}
                </div>
        <div className="navigation-buttons">
                    <button 
            className="prev-month" 
            onClick={handlePrevMonth} 
            aria-label={isWeeklyView ? "Previous Week" : "Previous Month"}
                    >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>
                    <button 
            className="next-month" 
            onClick={handleNextMonth}
            aria-label={isWeeklyView ? "Next Week" : "Next Month"}
                    >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                </div>
      </div>
    );
  }, [currentMonth, handlePrevMonth, handleNextMonth, isWeeklyView]);

  // Render the weekday headers
  const renderWeekdays = useCallback(() => {
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return (
                    <div className="weekday-row">
        {weekdays.map(day => (
                            <div key={day} className="weekday-header">{day}</div>
                        ))}
            </div>
        );
  }, []);
    
  // Render the calendar days
  const renderCalendarDays = useCallback(() => {
    let monthStart, monthEnd, startDate, endDate;
    
    if (isWeeklyView) {
      // In weekly view, just show current week
      startDate = startOfWeek(currentMonth);
      endDate = endOfWeek(currentMonth);
      monthStart = startOfMonth(currentMonth); // Still need this for isCurrentMonth check
    } else {
      // In monthly view, show the whole month
      monthStart = startOfMonth(currentMonth);
      monthEnd = endOfMonth(monthStart);
      startDate = startOfWeek(monthStart);
      endDate = endOfWeek(monthEnd);
    }
        
        const rows = [];
        let days = [];
        let day = startDate;
        
        // Create calendar days
        while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const clonedDay = new Date(day);
        const formattedDay = format(clonedDay, 'd');
        const dateStr = format(clonedDay, 'yyyy-MM-dd');
            const isCurrentMonth = isSameMonth(day, monthStart);
        const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
        const isToday = isSameDay(day, new Date());
        const isAvailable = isDateAvailable(clonedDay, selectedEvent);
        
        // Check if this date has any booked slots, regardless of event type
        const hasBookedSlots = Boolean(
          bookedSlotsByDay[dateStr]?.length > 0
        );
            
            let className = "calendar-day";
            if (!isCurrentMonth && !isWeeklyView) className += " empty"; // In weekly view, show all days
            if (isToday) className += " today";
            if (isSelected) className += " selected";
        if (isAvailable && isCurrentMonth) className += " has-slots";
        if (hasBookedSlots && isCurrentMonth) className += " has-booked";
        if (!isAvailable && isCurrentMonth) className += " no-slots";
            
            days.push(
                <div
                    className={className}
                    key={day.toString()}
                    onClick={() => {
                        if (isAvailable) {
                handleDateSelect(clonedDay);
              }
            }}
          >
            <span className="day-number">
              {isWeeklyView ? (
                // In weekly view, show day of week + date number
                <>
                  <span className="weekday-abbr">{format(clonedDay, 'EEE')}</span>
                  {' '}{formattedDay}
                </>
              ) : (
                // In monthly view, just show the date number
                isCurrentMonth ? formattedDay : ''
              )}
            </span>
            {hasBookedSlots && (
              <span className="booked-indicator" title="Has booked slots"></span>
                    )}
                </div>
            );
            
            day = addDays(day, 1);
      }
      
      rows.push(
        <div className="calendar-row" key={days[0].key}>
          {days}
            </div>
        );
      
      days = [];
      
      // In weekly view, we only show a single row (the current week)
      if (isWeeklyView) break;
    }
    
    return <div className="calendar-grid">{rows}</div>;
  }, [currentMonth, selectedDate, selectedEvent, isDateAvailable, handleDateSelect, bookedSlotsByDay]);

  // Render the time slots
  const renderTimeSlots = useCallback(() => {
    if (isLoading) {
        return (
        <div className="time-slots-loading">
          <div className="loading-spinner"></div>
          <p>Loading available times...</p>
            </div>
        );
    }
    
    if (noSlotsAvailable || timeSlots.length === 0) {
        return (
        <div className="no-slots-available">
          <p>No time slots available for the selected date.</p>
          <p>Please select another date.</p>
            </div>
        );
            }
        
        return (
      <div className="time-slots-grid">
        {timeSlots.map(time => {
          // Determine time of day for styling
          const hourGroup = getTimeOfDay(time);
          
                return (
                            <button 
              key={time}
              className={`time-slot ${selectedTimeSlot === time ? 'selected' : ''}`}
              onClick={() => handleTimeSelect(time)}
              data-hour-group={hourGroup}
            >
              {formatTimeForDisplay(time)}
                            </button>
          );
        })}
                    </div>
                );
  }, [isLoading, noSlotsAvailable, timeSlots, selectedTimeSlot, handleTimeSelect]);

  // Render the component
        return (
    <div className={`cal-calendar-view ${forceLightMode ? 'force-light' : ''}`}>
      <div className="calendar-layout">
        {/* Meeting Types Column */}
        <div className="meeting-types-column">
          <h3 className="meeting-types-header">Select Meeting Type</h3>
          
          {/* Mobile dropdown for event types */}
          <select 
            className="meeting-types-dropdown" 
            onChange={(e) => {
              const selectedEvent = events.find(event => 
                String(event.id) === e.target.value
              );
              if (selectedEvent) {
                handleEventSelect(selectedEvent);
              }
            }}
            value={selectedEvent?.id || ''}
          >
            <option value="" disabled>Select a meeting type</option>
            {events.map(event => {
              const eventId = event.id;
              const eventName = 'name' in event ? event.name : event.title;
              const eventDuration = 'duration' in event ? event.duration : ('length' in event ? event.length : 30);
        
        return (
                <option key={eventId} value={eventId}>
                  {eventName} ({eventDuration} min)
                </option>
                        );
                    })}
          </select>
          
          {/* Desktop list view for event types */}
          <div className="meeting-type-items-container">
            {events.map(event => {
              // Determine event properties safely
              const eventId = event.id;
              const eventName = 'name' in event ? event.name : event.title;
              const eventTitle = event.title;
              const eventDescription = event.description;
              const eventDuration = 'duration' in event ? event.duration : ('length' in event ? event.length : 30);
              
    return (
                <div 
                  key={eventId} 
                  className={`meeting-type-item ${selectedEvent && selectedEvent.id === eventId ? 'selected' : ''}`}
                                onClick={() => handleEventSelect(event)}
                            >
                  <div className="meeting-type-name">{eventName || eventTitle}</div>
                                
                                <div className="meeting-type-duration">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="10"></circle>
                                        <polyline points="12 6 12 12 16 14"></polyline>
                                    </svg>
                    <span>{eventDuration} min</span>
                                </div>
                                
                  {eventDescription && (
                    <div className="meeting-type-description">{eventDescription}</div>
                                )}
                            </div>
              );
            })}
          </div>
                    </div>
                    
                    {/* Calendar Section */}
                    <div className="calendar-section">
          {renderCalendarHeader()}
          <div className="calendar-wrapper">
            {renderWeekdays()}
            {renderCalendarDays()}
          </div>
                    </div>
                    
                    {/* Time Slots Column */}
                    <div className="time-slots-column">
                        <div className="time-slots-section">
                            {selectedDate && (
                                <h3 className="time-slots-header">
                Available Times for {format(selectedDate, 'EEEE, MMMM d, yyyy')}
                                </h3>
                            )}
                            
            {selectedEvent && (
              <div className="event-duration-info">
                <span>Meeting duration: {selectedEvent.duration || selectedEvent.length || 30} minutes</span>
                <div className="time-format-info">Times displayed in {timezone}</div>
                                </div>
            )}
            
                                <div className="time-slots-container scrollable">
                                    {renderTimeSlots()}
                                </div>
                            
            <div className="timezone-info">
              <small>Times are shown in your local timezone: {timezone}</small>
                        </div>
                    </div>
                </div>
      </div>
        </div>
    );
};

// Export the component both ways to ensure it can be imported correctly
export { CalendarView };  // Named export
export default CalendarView;  // Default export